/**
 * Le planning de la semaine, vu par l'écran — le calcul, hors de React.
 *
 * Même découpe que `ot.ts`, `affectation.ts` et `parc.ts`, et pour la même
 * raison : ce qui est ici se teste sans navigateur, sans base et sans session.
 *
 * QUATRE CHOSES QUE CE FICHIER SAIT
 * ---------------------------------------------------------------------------
 *
 *   - **une semaine est un calcul de dates, pas un objet `Date`.** Tout se fait
 *     en chaînes `AAAA-MM-JJ`, et les seuls `Date` construits ici le sont en
 *     UTC. Un `new Date('2026-09-04')` interprété dans le fuseau du navigateur
 *     donne le 3 septembre à l'ouest de Greenwich — ce genre de décalage a déjà
 *     coûté une soirée sur les ordres de transport, le 04/09/2026 ;
 *
 *   - **une case de la grille, c'est un camion ET un jour.** Les étapes
 *     arrivent à plat ; c'est ici qu'elles se rangent en blocs ;
 *
 *   - **un bloc montre TOUT l'ordre, pas seulement son jour.** Les étapes des
 *     autres jours restent lisibles, en retrait, avec leur date. C'est ce qui
 *     permet de comprendre un transport sans l'ouvrir — repris de la v1 ;
 *
 *   - **un dépôt peut être refusé, et le refus s'explique.** Jamais un bloc qui
 *     ne bouge pas sans qu'on sache pourquoi : chaque refus rend une phrase.
 */

import { aplatir } from './annuaire.js';
import { classeStatut } from './ot.js';

// ---------------------------------------------------------------------------
// Ce que l'API rend
// ---------------------------------------------------------------------------

/** Une ligne du planning : un camion, avec l'attelage du jour de référence. */
export interface VehiculePlanning {
  readonly id: string;
  readonly immatriculation: string;
  readonly rang_planning: number | null;
  readonly chauffeur_id: string | null;
  readonly chauffeur: string | null;
  readonly remorque_id: string | null;
  readonly remorque: string | null;
}

/** Une étape, telle que le planning la lit — allégée de ce qu'il n'affiche pas. */
export interface EtapePlanning {
  readonly id: string;
  readonly ot_id: string;
  readonly numero: string;
  readonly statut: string;
  readonly client: string;
  readonly rang: number;
  readonly type: string;
  readonly site_nom: string;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly fait: boolean;
  readonly litige_ouvert: boolean;
  /**
   * La tournée de cette étape, en clair, ou `null`. Décidé le 04/09/2026 :
   * « le planning affiche le numéro de tournée sur le bloc ».
   */
  readonly tournee_id?: string | null;
  readonly tournee?: string | null;
  readonly vehicule_id: string | null;
  readonly vehicule: string | null;
  readonly chauffeur_id: string | null;
  readonly chauffeur: string | null;
  readonly remorque_id: string | null;
  readonly remorque: string | null;
}

/** Un support tel que la société l'a réglé : son nom, et son mot court. */
export interface SupportDit {
  readonly nom: string;
  readonly abrege: string | null;
}

export interface Planning {
  readonly planning: {
    readonly du: string;
    readonly au: string;
    readonly reference: string;
  };
  readonly vehicules: readonly VehiculePlanning[];
  readonly etapes: readonly EtapePlanning[];
  readonly a_affecter: readonly EtapePlanning[];
  /**
   * Les supports de la société, avec leur abrégé. Ils voyagent avec le
   * planning : la grille écrit « 33 pal » et l'abrégé appartient désormais à la
   * société, non plus au code.
   */
  readonly supports: readonly SupportDit[];
}

// ---------------------------------------------------------------------------
// Les jours
// ---------------------------------------------------------------------------

const MOIS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

const JOURS_COURTS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];

const FORME = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Le jour en UTC, ou `null` si la chaîne n'est pas une date. */
function enDate(jour: string): Date | null {
  const morceaux = FORME.exec(jour);
  if (morceaux === null) return null;
  const [a, m, j] = [Number(morceaux[1]), Number(morceaux[2]), Number(morceaux[3])];
  const date = new Date(Date.UTC(a, m - 1, j));
  // Recomposition : « 2026-02-31 » a la bonne forme et n'existe pas.
  if (
    date.getUTCFullYear() !== a ||
    date.getUTCMonth() !== m - 1 ||
    date.getUTCDate() !== j
  ) {
    return null;
  }
  return date;
}

function enChaine(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** « 2026-09-04 » et 3 → « 2026-09-07 ». Rend `null` si le jour est illisible. */
export function decaler(jour: string, jours: number): string | null {
  const date = enDate(jour);
  if (date === null) return null;
  date.setUTCDate(date.getUTCDate() + jours);
  return enChaine(date);
}

/** Le nombre de jours de l'un à l'autre. Négatif si le second est avant. */
export function ecartEnJours(du: string, au: string): number | null {
  const a = enDate(du);
  const b = enDate(au);
  if (a === null || b === null) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/**
 * Le lundi de la semaine d'un jour.
 *
 * La semaine française commence le lundi ; `getUTCDay()` rend 0 le dimanche,
 * qu'il faut donc ramener à 7 — sans quoi le dimanche ouvrirait la semaine
 * SUIVANTE, et l'exploitation verrait le planning du lundi d'après en arrivant
 * le dimanche soir.
 */
export function lundiDe(jour: string): string | null {
  const date = enDate(jour);
  if (date === null) return null;
  const numero = date.getUTCDay() === 0 ? 7 : date.getUTCDay();
  date.setUTCDate(date.getUTCDate() - (numero - 1));
  return enChaine(date);
}

export interface Semaine {
  readonly du: string;
  readonly au: string;
  readonly jours: readonly string[];
}

/**
 * La semaine affichée : cinq jours par défaut, sept sur demande.
 *
 * Cinq, parce que TNS ne travaille jamais le dimanche et au maximum deux
 * samedis par an — décidé le 04/09/2026, et c'est l'inverse de la v1, qui
 * ouvrait sur sept jours dont deux presque toujours vides.
 */
export function semaineDe(jour: string, septJours = false): Semaine | null {
  const lundi = lundiDe(jour);
  if (lundi === null) return null;
  const jours: string[] = [];
  for (let index = 0; index < (septJours ? 7 : 5); index += 1) {
    jours.push(decaler(lundi, index) as string);
  }
  return {
    du: lundi,
    au: jours[jours.length - 1] as string,
    jours,
  };
}

/** « 2026-08-31 » → « lun. 31/08 ». En-tête d'une colonne. */
export function colonneDite(jour: string): string | null {
  const date = enDate(jour);
  if (date === null) return null;
  return `${JOURS_COURTS[date.getUTCDay()] as string} ${jour.slice(8)}/${jour.slice(5, 7)}`;
}

/**
 * « Semaine du 31 août au 4 septembre 2026 ».
 *
 * Le mois n'est répété que s'il change, et l'année n'apparaît qu'une fois : une
 * semaine à cheval sur deux années les porte toutes les deux.
 */
export function semaineDite(du: string, au: string): string | null {
  const debut = enDate(du);
  const fin = enDate(au);
  if (debut === null || fin === null) return null;

  const moisDebut = MOIS[debut.getUTCMonth()] as string;
  const moisFin = MOIS[fin.getUTCMonth()] as string;
  const anneeDebut = debut.getUTCFullYear();
  const anneeFin = fin.getUTCFullYear();

  const gauche =
    anneeDebut !== anneeFin
      ? `${debut.getUTCDate()} ${moisDebut} ${anneeDebut}`
      : moisDebut === moisFin
        ? `${debut.getUTCDate()}`
        : `${debut.getUTCDate()} ${moisDebut}`;

  return `Semaine du ${gauche} au ${fin.getUTCDate()} ${moisFin} ${anneeFin}`;
}

/** Samedi ou dimanche : la colonne prend un fond plus sourd. */
export function finDeSemaine(jour: string): boolean {
  const date = enDate(jour);
  if (date === null) return false;
  return date.getUTCDay() === 0 || date.getUTCDay() === 6;
}

/** « 08:00 » reste « 08:00 » ; `null` reste `null`. Créneau : « 08:00-10:00 ». */
export function heureDite(debut: string | null, fin: string | null): string | null {
  if (debut === null && fin === null) return null;
  if (debut !== null && fin !== null && fin !== debut) return `${debut}-${fin}`;
  return debut ?? fin;
}

// ---------------------------------------------------------------------------
// La marchandise
// ---------------------------------------------------------------------------

/**
 * Les abrégés, rangés par nom de support, sans casse.
 *
 * Ils VIENNENT DE LA SOCIÉTÉ depuis le 04/09/2026 (migration 20260904170000) :
 * ils étaient jusque-là écrits dans ce fichier, donc imposés à tous les clients
 * de Stafa. Un support sans abrégé, ou absent de la liste — parce qu'une vieille
 * étape porte un mot depuis supprimé — est rendu TEL QUEL : mieux vaut un texte
 * long, quitte à ce qu'il se coupe, qu'un raccourci inventé par le logiciel.
 */
export function abregesDe(
  supports: readonly SupportDit[] | null | undefined,
): ReadonlyMap<string, string> {
  const table = new Map<string, string>();
  // Rien plutôt qu'une page blanche : une API d'une version antérieure ne rend
  // pas cette liste, et l'écran doit continuer à dessiner le planning sans
  // abréger. Constaté sur le poste le 04/09/2026, écran mis en ligne avant
  // l'API.
  for (const support of supports ?? []) {
    if (support.abrege !== null && support.abrege.trim() !== '') {
      table.set(support.nom.trim().toLowerCase(), support.abrege.trim());
    }
  }
  return table;
}

/**
 * « 33 » et « Palette 80×120 » → « 33 pal ».
 *
 * La case du planning ne laisse que soixante-trois pixels à la quantité —
 * mesuré le 04/09/2026. C'est toute la raison de l'abrégé.
 */
export function marchandiseDite(
  quantite: string | null,
  support: string | null,
  abreges: ReadonlyMap<string, string> = new Map(),
): string | null {
  if (quantite === null || quantite.trim() === '') {
    return support === null || support.trim() === '' ? null : support;
  }

  // `numeric` arrive en chaîne : « 33.00 » se lit « 33 », « 4.60 » se lit
  // « 4,6 ». Jamais converti en flottant, comme partout dans ce dépôt.
  const nombre = quantite.replace(/\.?0+$/, '').replace('.', ',');
  const abrege =
    support === null || support.trim() === ''
      ? null
      : (abreges.get(support.trim().toLowerCase()) ?? support.trim());

  return abrege === null ? nombre : `${nombre} ${abrege}`;
}

// ---------------------------------------------------------------------------
// Les blocs de la grille
// ---------------------------------------------------------------------------

/** Une ligne d'étape dans un bloc. */
export interface LigneBloc {
  readonly id: string;
  readonly chargement: boolean;
  readonly site: string;
  readonly departement: string | null;
  readonly heure: string | null;
  readonly marchandise: string | null;
  readonly fait: boolean;
  readonly litige: boolean;
  /** Vraie quand l'étape a lieu un AUTRE jour : la ligne s'affiche en retrait. */
  readonly autre: boolean;
  readonly jour: string;
}

/** Un ordre de transport, dessiné dans une case de la grille. */
export interface Bloc {
  readonly ot_id: string;
  readonly numero: string;
  readonly client: string;
  readonly statut: string;
  readonly classe: string;
  readonly litige: boolean;
  readonly jour: string;
  /**
   * LE NUMÉRO DE LA TOURNÉE, quand toutes les étapes de ce jour en portent la
   * MÊME. Deux tournées différentes dans une même case donnent `null` : écrire
   * l'une des deux laisserait croire que l'autre n'existe pas, et c'est
   * précisément ce qui rendrait le refus de déplacement incompréhensible.
   */
  readonly tournee: string | null;
  readonly vehicule_id: string | null;
  /** Le camion des étapes de CE jour, quand il n'est pas celui de la ligne. */
  readonly repris_par: string | null;
  readonly suite_avant: boolean;
  readonly suite_apres: boolean;
  readonly lignes: readonly LigneBloc[];
}

/**
 * Le numéro de tournée d'un groupe d'étapes — s'il n'y en a qu'un.
 *
 * `null` dès que les étapes se partagent entre plusieurs tournées, ou qu'une
 * seule n'en a pas. Un numéro affiché sur une case où toutes les étapes ne sont
 * pas dans cette tournée serait un mensonge d'un genre coûteux : c'est
 * justement sur ce numéro que l'exploitant ira chercher l'étape qu'il ne peut
 * pas déplacer.
 */
function uneSeuleTournee(etapes: readonly EtapePlanning[]): string | null {
  const noms = new Set(etapes.map((e) => e.tournee ?? null));
  if (noms.size !== 1) return null;
  const [seul] = [...noms];
  return seul ?? null;
}

/** Le département se lit du code postal : « 28630 » → « 28 ». */
export function departement(codePostal: string | null): string | null {
  if (codePostal === null) return null;
  const propre = codePostal.trim();
  if (!/^\d{4,5}$/.test(propre)) return null;
  return propre.length === 4 ? `0${propre.slice(0, 1)}` : propre.slice(0, 2);
}

function ligneDe(
  etape: EtapePlanning,
  jour: string,
  abreges: ReadonlyMap<string, string>,
): LigneBloc {
  return {
    id: etape.id,
    chargement: etape.type === 'chargement',
    site: etape.site_ville ?? etape.site_nom,
    departement: departement(etape.site_code_postal),
    heure: heureDite(etape.heure_debut, etape.heure_fin),
    marchandise: marchandiseDite(etape.quantite, etape.support, abreges),
    fait: etape.fait,
    litige: etape.litige_ouvert,
    autre: etape.jour !== jour,
    jour: etape.jour,
  };
}

/**
 * Les blocs, rangés par case.
 *
 * La clé est `camion|jour` : c'est exactement une case de la grille. Les étapes
 * SANS camion ne font pas de bloc — elles n'ont pas de case où aller ; elles
 * apparaissent en retrait dans le bloc des jours affectés du même ordre.
 *
 * `repris_par` dit qu'un AUTRE camion a fait la suite : c'est le relais, et
 * c'est la seule information de la grille qui parle d'une autre ligne.
 */
export function blocsParCase(
  etapes: readonly EtapePlanning[],
  abreges: ReadonlyMap<string, string> = new Map(),
): ReadonlyMap<string, readonly Bloc[]> {
  const parOrdre = new Map<string, EtapePlanning[]>();
  for (const etape of etapes) {
    const liste = parOrdre.get(etape.ot_id) ?? [];
    liste.push(etape);
    parOrdre.set(etape.ot_id, liste);
  }

  const cases = new Map<string, Bloc[]>();

  for (const [otId, toutes] of parOrdre) {
    const rangees = [...toutes].sort((a, b) => a.rang - b.rang);
    const premiere = rangees[0] as EtapePlanning;

    // Les camions de cet ordre, jour par jour. Deux jours sur deux camions
    // différents : c'est un relais.
    const camions = new Set(
      rangees.filter((e) => e.vehicule_id !== null).map((e) => e.vehicule_id),
    );

    const posees = new Map<string, EtapePlanning[]>();
    for (const etape of rangees) {
      if (etape.vehicule_id === null) continue;
      const cle = `${etape.vehicule_id}|${etape.jour}`;
      const liste = posees.get(cle) ?? [];
      liste.push(etape);
      posees.set(cle, liste);
    }

    for (const [cle, duJour] of posees) {
      const jour = (duJour[0] as EtapePlanning).jour;
      const vehicule = (duJour[0] as EtapePlanning).vehicule_id;
      const autres = rangees.filter((e) => e.vehicule_id !== vehicule);

      const bloc: Bloc = {
        ot_id: otId,
        numero: premiere.numero,
        client: premiere.client,
        statut: premiere.statut,
        classe: classeStatut(premiere.statut),
        litige: rangees.some((e) => e.litige_ouvert),
        jour,
        tournee: uneSeuleTournee(duJour),
        vehicule_id: vehicule,
        repris_par:
          camions.size > 1
            ? (autres.find((e) => e.vehicule !== null)?.vehicule ?? null)
            : null,
        suite_avant: rangees.some((e) => e.jour < jour),
        suite_apres: rangees.some((e) => e.jour > jour),
        lignes: rangees.map((etape) => ligneDe(etape, jour, abreges)),
      };

      const liste = cases.get(cle) ?? [];
      liste.push(bloc);
      cases.set(cle, liste);
    }
  }

  // Dans une case, les blocs se suivent par heure de la première étape du jour,
  // puis par numéro : l'ordre du matin en haut.
  for (const liste of cases.values()) {
    liste.sort((a, b) => {
      const heureA = a.lignes.find((l) => !l.autre)?.heure ?? '';
      const heureB = b.lignes.find((l) => !l.autre)?.heure ?? '';
      return heureA === heureB
        ? a.numero.localeCompare(b.numero)
        : heureA.localeCompare(heureB);
    });
  }

  return cases;
}

// ---------------------------------------------------------------------------
// La pile des ordres à affecter
// ---------------------------------------------------------------------------

export interface CartePile {
  readonly ot_id: string;
  readonly numero: string;
  readonly client: string;
  readonly statut: string;
  readonly classe: string;
  readonly litige: boolean;
  readonly du: string;
  readonly au: string;
  readonly lignes: readonly LigneBloc[];
}

export interface GroupePile {
  readonly titre: string;
  readonly cartes: readonly CartePile[];
}

/** « lun. 07/09 » ou « jeu. 03/09 → ven. 04/09 » : les dates de l'ordre. */
export function plageDite(du: string, au: string): string | null {
  const gauche = colonneDite(du);
  if (gauche === null) return null;
  if (du === au) return gauche;
  const droite = colonneDite(au);
  return droite === null ? gauche : `${gauche} → ${droite}`;
}

/**
 * La pile, groupée par dates.
 *
 * Les ordres se suivent par date de début : ce qui part demain est en haut. Un
 * ordre sans étape n'y figure pas — l'API ne le rend pas, et il n'aurait ni
 * date ni trajet à afficher.
 */
export function pile(
  etapes: readonly EtapePlanning[],
  abreges: ReadonlyMap<string, string> = new Map(),
): readonly GroupePile[] {
  const parOrdre = new Map<string, EtapePlanning[]>();
  for (const etape of etapes) {
    const liste = parOrdre.get(etape.ot_id) ?? [];
    liste.push(etape);
    parOrdre.set(etape.ot_id, liste);
  }

  const cartes: CartePile[] = [];
  for (const [otId, toutes] of parOrdre) {
    const rangees = [...toutes].sort((a, b) => a.rang - b.rang);
    const premiere = rangees[0] as EtapePlanning;
    const jours = rangees.map((e) => e.jour).sort();
    cartes.push({
      ot_id: otId,
      numero: premiere.numero,
      client: premiere.client,
      statut: premiere.statut,
      classe: classeStatut(premiere.statut),
      litige: rangees.some((e) => e.litige_ouvert),
      du: jours[0] as string,
      au: jours[jours.length - 1] as string,
      lignes: rangees.map((etape) => ligneDe(etape, etape.jour, abreges)),
    });
  }

  cartes.sort((a, b) =>
    a.du === b.du ? a.numero.localeCompare(b.numero) : a.du < b.du ? -1 : 1,
  );

  const groupes: GroupePile[] = [];
  for (const carte of cartes) {
    const titre = plageDite(carte.du, carte.au) ?? carte.du;
    const dernier = groupes[groupes.length - 1];
    if (dernier !== undefined && dernier.titre === titre) {
      (dernier.cartes as CartePile[]).push(carte);
    } else {
      groupes.push({ titre, cartes: [carte] });
    }
  }
  return groupes;
}

// ---------------------------------------------------------------------------
// Le dépôt
// ---------------------------------------------------------------------------

/**
 * Ce qu'on tient pendant le glissement.
 *
 * `jour` est le jour de la CASE d'où part le bloc, et `null` quand la carte
 * vient de la pile : elle n'est dans aucune case, et la poser affecte l'ordre
 * entier.
 *
 * `debut` est le premier jour de l'ordre, toujours renseigné. Il sert à une
 * seule question, mais elle compte : lâcher une carte de la pile sur la colonne
 * du jeudi alors que le transport est daté du mardi doit demander s'il faut le
 * REPORTER, sans quoi l'écran afficherait un transport du mardi dans la colonne
 * du jeudi — exactement le mensonge qu'on cherche à éviter.
 */
export interface Prise {
  readonly ot_id: string;
  readonly numero: string;
  readonly jour: string | null;
  readonly debut: string;
  readonly statut: string;
  readonly valide: boolean;
  /**
   * La tournée d'où vient le bloc, quand il n'y en a qu'une. Sert au REFUS :
   * le planning refuse de déplacer une étape en tournée, et sans ce numéro il
   * ne dirait pas où aller la sortir. Un refus qu'on ne sait pas lever n'est
   * pas un refus, c'est une panne.
   */
  readonly tournee: string | null;
}

/** Le jour dont on part : celui de la case, ou le début de l'ordre. */
function jourDePart(prise: Prise): string {
  return prise.jour ?? prise.debut;
}

/**
 * Ce que le dépôt refuse, et la phrase qui le dit.
 *
 * Rend `null` quand le dépôt est permis — c'est le cas le plus fréquent, et il
 * ne se raconte pas.
 *
 * Ces refus doublent ceux de l'API : la même garantie tenue deux fois, une fois
 * en base et une fois en mots. C'est la règle du dépôt, et elle vaut ici parce
 * qu'un refus arrivé après un aller-retour laisserait le bloc bouger à l'écran
 * puis revenir en arrière.
 */
export function refusDepot(prise: Prise, jourVise: string): string | null {
  if (prise.statut === 'annule') {
    return 'Cet ordre est annulé : il ne se planifie pas.';
  }
  if (jourDePart(prise) !== jourVise && prise.valide) {
    return 'Une étape de cet ordre est déjà validée : ses dates ne se déplacent plus.';
  }
  return null;
}

/** Le report demande une confirmation dès que le jour change. */
export function reportDemande(prise: Prise, jourVise: string): boolean {
  return jourDePart(prise) !== jourVise;
}

/**
 * L'ordre des lignes après un déplacement.
 *
 * Rendu comme une liste d'identifiants, prête pour la route
 * `PUT /planning/ordre-des-vehicules`. Un identifiant inconnu ne bouge rien :
 * l'écran ne peut pas ranger un camion qu'il n'affiche pas.
 */
export function ordreApres(
  vehicules: readonly VehiculePlanning[],
  deplace: string,
  avant: string | null,
): readonly string[] {
  const ids = vehicules.map((v) => v.id);
  if (!ids.includes(deplace) || (avant !== null && !ids.includes(avant))) {
    return ids;
  }
  const sansLui = ids.filter((id) => id !== deplace);
  if (avant === null) {
    return [...sansLui, deplace];
  }
  const place = sansLui.indexOf(avant);
  return [...sansLui.slice(0, place), deplace, ...sansLui.slice(place)];
}

/**
 * Ce que la tête de ligne affiche.
 *
 * Le NOM DU CHAUFFEUR d'abord — décidé le 04/09/2026 : « le planning doit
 * indiquer le nom du chauffeur en info principal et pas le tracteur ». Un
 * camion sans chauffeur attelé garde sa ligne et le dit : le faire disparaître
 * cacherait un camion disponible.
 */
export function teteDeLigne(vehicule: VehiculePlanning): {
  readonly principal: string;
  readonly sansChauffeur: boolean;
  readonly materiel: string;
} {
  const materiel =
    vehicule.remorque === null
      ? vehicule.immatriculation
      : `${vehicule.immatriculation} + ${vehicule.remorque}`;
  return {
    principal: vehicule.chauffeur ?? 'Sans chauffeur',
    sansChauffeur: vehicule.chauffeur === null,
    materiel,
  };
}

/**
 * La recherche : le seul filtre de l'écran, décidé le 04/09/2026 — « des
 * filtres beaucoup plus simples, là il y en a trop ».
 *
 * Elle porte sur ce qu'un bloc AFFICHE : son numéro, son client, ses villes et
 * leurs départements. Le camion n'y est pas — c'est la ligne, et une ligne se
 * trouve à l'œil. Sans accent ni casse, avec la fonction de l'annuaire plutôt
 * qu'une seconde à côté : deux façons d'aplatir un texte finiraient par ne plus
 * trouver les mêmes choses.
 */
export function correspond(bloc: Bloc | CartePile, recherche: string): boolean {
  const cherche = aplatir(recherche).trim();
  if (cherche === '') return true;
  const matiere = [
    bloc.numero,
    bloc.client,
    ...bloc.lignes.map((l) => l.site),
    ...bloc.lignes.map((l) => l.departement ?? ''),
  ].join(' ');
  return aplatir(matiere).includes(cherche);
}
